import { RefreshCcw } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingSummary } from '@/billing/api/FetchBillingList';
import { useBillingProcesses } from '@/billing/hooks/useBillingProcesses';
import { useIsLockedByOtherUser } from '@/billing/hooks/useIsLockedByOtherUser';
import { useSelectedBillingProcess } from '@/billing/hooks/useSelectedBillingProcess';
import { Button } from '@/common/components/ui/button';
import { Label } from '@/common/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';

type ProcessSelectProps = {
  billing: BillingSummary;
  value?: string;
  onValueChange: (processCode: string) => void;
};

export const ProcessSelect = ({
  billing,
  value,
  onValueChange,
}: ProcessSelectProps) => {
  const { t } = useTranslation();
  const triggerId = useId();
  const { processes, isLoading, isError, refetch } = useBillingProcesses();
  const { process, isInferred } = useSelectedBillingProcess(billing, value);
  const isLocked = useIsLockedByOtherUser(billing);

  return (
    <div className="flex flex-col gap-1.5">
      <Label
        htmlFor={triggerId}
        className="text-sm font-medium text-muted-foreground"
      >
        {t('billing.drawer.process.label')}
      </Label>

      <Select
        value={process?.code ?? ''}
        onValueChange={onValueChange}
        disabled={isLoading || isError || isLocked}
      >
        <SelectTrigger id={triggerId} className="w-full bg-background">
          <SelectValue placeholder={t('billing.drawer.process.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {processes.map((process) => (
            <SelectItem key={process.code} value={process.code}>
              {process.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isInferred && (
        <p className="text-xs text-muted-foreground">
          {t('billing.drawer.process.inferredFrom', {
            activityCode: billing.activityCode,
          })}
        </p>
      )}

      {isError && (
        <div className="flex items-center gap-1">
          <span role="alert" className="text-xs text-destructive">
            {t('billing.drawer.process.loadError')}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('billing.drawer.retry')}
            onClick={() => refetch()}
          >
            <RefreshCcw className="stroke-ring" />
          </Button>
        </div>
      )}
    </div>
  );
};
